import type { TFunction } from 'i18next'

import { CreateObjectStepId } from 'entities/kyrgyzstan/model/create-object'

import type {
  EditObjectDraftRevision,
  EditObjectDynamicField,
} from 'shared/api/kyrgyzstan/edit-object'
import { getCurrencyTextSymbol } from 'shared/lib/getCurrencyTextSymbol'
import { priceShow } from 'shared/lib/priceShow'
import { AllIcons } from 'shared/ui/app-icon/types'

export interface EditObjectListItem {
  id: string
  title: string
  subtitle?: string
  icon: AllIcons
  stepId: CreateObjectStepId
}

const editObjectKey = 'kyrgyzstan_block.edit_object'

const hasDynamicFieldValue = (value: EditObjectDynamicField['value']) => {
  if (value === null || value === undefined) {
    return false
  }

  if (typeof value === 'boolean') {
    return value
  }

  const stringValue = String(value).trim().toLowerCase()

  return stringValue.length > 0 && stringValue !== 'false'
}

const getSelectedAmenitiesCount = (
  dynamicFields: EditObjectDraftRevision['dynamic_fields'],
) => {
  if (dynamicFields?.field_ids) {
    return dynamicFields.field_ids.length
  }

  return (dynamicFields?.fields ?? []).filter((field) =>
    hasDynamicFieldValue(field.value),
  ).length
}

const getAddressSubtitle = (
  address: EditObjectDraftRevision['address'],
  t: TFunction,
) => {
  if (!address) {
    return undefined
  }

  const parts: string[] = []

  if (address.city_name) {
    parts.push(
      t(`${editObjectKey}.menu.address.city`, {
        city: address.city_name,
      }),
    )
  }

  if (address.street) {
    parts.push(
      t(`${editObjectKey}.menu.address.street`, {
        street: address.street,
      }),
    )
  }

  if (address.house_number) {
    parts.push(address.house_number)
  }

  return parts.join(', ') || undefined
}

const getDescriptionSubtitle = (
  description: EditObjectDraftRevision['description'],
  t: TFunction,
) => {
  if (!description) {
    return undefined
  }

  const parts: string[] = []

  if (description.title) {
    parts.push(description.title)
  }

  if (description.capacity) {
    parts.push(
      t(`${editObjectKey}.menu.description.capacity`, {
        count: description.capacity,
      }),
    )
  }

  return parts.join(', ') || undefined
}

const getPriceSubtitle = (price: EditObjectDraftRevision['price']) => {
  if (!price?.base_price) {
    return undefined
  }

  return `${priceShow(price.base_price)} ${getCurrencyTextSymbol('KGS')}`
}

const getConditionsSubtitle = (
  conditions: EditObjectDraftRevision['rules'],
  t: TFunction,
) => {
  if (!conditions) {
    return undefined
  }

  const parts: string[] = []

  if (conditions.arrival_time_from && conditions.arrival_time_to) {
    parts.push(
      t(`${editObjectKey}.menu.conditions.check_in`, {
        from: conditions.arrival_time_from,
        to: conditions.arrival_time_to,
      }),
    )
  }

  if (conditions.departure_time_before) {
    parts.push(
      t(`${editObjectKey}.menu.conditions.check_out`, {
        time: conditions.departure_time_before,
      }),
    )
  }

  return parts.join(', ') || undefined
}

export const buildEditObjectMenuItemsFromRevision = (
  revision: EditObjectDraftRevision | null,
  t: TFunction,
): EditObjectListItem[] => {
  if (!revision) {
    return []
  }

  return [
    {
      id: 'photos',
      title: t(`${editObjectKey}.menu.photos.title`),
      subtitle: t(`${editObjectKey}.menu.photos.subtitle`, {
        count: revision.photos?.length ?? 0,
      }),
      icon: AllIcons.GALLERY,
      stepId: CreateObjectStepId.Photos,
    },
    {
      id: 'address',
      title: t(`${editObjectKey}.menu.address.title`),
      subtitle: getAddressSubtitle(revision.address, t),
      icon: AllIcons.LOCATION,
      stepId: CreateObjectStepId.Address,
    },
    {
      id: 'amenities',
      title: t(`${editObjectKey}.menu.amenities.title`),
      subtitle: t(`${editObjectKey}.menu.amenities.subtitle`, {
        count: getSelectedAmenitiesCount(revision.dynamic_fields),
      }),
      icon: AllIcons.HEART,
      stepId: CreateObjectStepId.Amenities,
    },
    {
      id: 'description',
      title: t(`${editObjectKey}.menu.description.title`),
      subtitle: getDescriptionSubtitle(revision.description, t),
      icon: AllIcons.LEFT_ALIGNMENT,
      stepId: CreateObjectStepId.Description,
    },
    {
      id: 'price',
      title: t(`${editObjectKey}.menu.price.title`),
      subtitle: getPriceSubtitle(revision.price),
      icon: AllIcons.MONEY,
      stepId: CreateObjectStepId.Price,
    },
    {
      id: 'conditions',
      title: t(`${editObjectKey}.menu.conditions.title`),
      subtitle: getConditionsSubtitle(revision.rules, t),
      icon: AllIcons.INFO_TRIANGLE_OUTLINED,
      stepId: CreateObjectStepId.Conditions,
    },
  ]
}
